import { createElement, useCallback, useState, type MouseEvent as ReactMouseEvent } from "react";
import AuthModal from "../components/AuthModal";
import { sanitizeAuthSource, sanitizeInternalReturnTo } from "../utils/authReturn";
import { track } from "../utils/analytics";

/**
 * The free pages' sign-in modal. `openAuth` replaces navigating to /get-started; put
 * `interceptAuthLinks` on a wrapper's onClickCapture so every <a href="/get-started?..."> inside opens
 * the modal with that link's return path and source. Modified clicks (new tab, etc.) still navigate.
 */
export function useAuthModal() {
  const [target, setTarget] = useState<{ returnTo: string; source: string } | null>(null);
  const close = useCallback(() => setTarget(null), []);
  const openAuth = useCallback((returnTo: string | null | undefined, source: string | null | undefined) => {
    const safeSource = sanitizeAuthSource(source) || "";
    setTarget({ returnTo: sanitizeInternalReturnTo(returnTo) || "", source: safeSource });
    track("auth_modal_opened", { surface: "web", source: safeSource || "direct" });
  }, []);
  const interceptAuthLinks = useCallback(
    (event: ReactMouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor || anchor.target === "_blank") return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin || url.pathname !== "/get-started") return;
      event.preventDefault();
      openAuth(url.searchParams.get("returnTo"), url.searchParams.get("source"));
    },
    [openAuth],
  );
  const modal = target ? createElement(AuthModal, { returnTo: target.returnTo, source: target.source, onClose: close }) : null;
  return { openAuth, interceptAuthLinks, modal };
}
